import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Edit, Camera, Loader2, MapPin, Clock, Calendar, ChevronUp, ChevronDown, GripVertical, CalendarX, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { formatPrice, CATEGORIES } from '../../lib/utils';
import { upcomingClassDates, formatTrialDate } from '../../lib/trialDates';
import api from '../../lib/api';

const ClassesTab = ({
  classes,
  teachers,
  rooms,
  classForm,
  setClassForm,
  classModalOpen,
  setClassModalOpen,
  editingItem,
  setEditingItem,
  uploadingClassPhoto,
  handleClassPhotoUpload,
  handleClassSubmit,
  handleDeleteClass,
  handleReorderClass,
  editClass,
  resetClassForm,
  toggleTeacherAssignment,
  updateTeacherRole
}) => {
  // Global school-wide blackout dates modal state.
  const [blackoutOpen, setBlackoutOpen] = useState(false);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [blackoutInput, setBlackoutInput] = useState('');
  const [blackoutSaving, setBlackoutSaving] = useState(false);

  useEffect(() => {
    if (!blackoutOpen) return;
    api.get('/academy-info/trial-blackout-dates')
      .then((res) => setBlackoutDates(res.data?.dates || []))
      .catch(() => setBlackoutDates([]));
  }, [blackoutOpen]);

  const addBlackout = () => {
    const iso = (blackoutInput || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) { toast.error('Please enter a valid date'); return; }
    if (blackoutDates.includes(iso)) { setBlackoutInput(''); return; }
    setBlackoutDates([...blackoutDates, iso].sort());
    setBlackoutInput('');
  };
  const removeBlackout = (iso) => setBlackoutDates(blackoutDates.filter((d) => d !== iso));
  const saveBlackouts = async () => {
    setBlackoutSaving(true);
    try {
      const res = await api.put('/academy-info/trial-blackout-dates', { dates: blackoutDates });
      setBlackoutDates(res.data?.dates || []);
      toast.success('Blackout dates saved');
      setBlackoutOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setBlackoutSaving(false);
    }
  };

  return (
    <div data-testid="classes-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Classes</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBlackoutOpen(true)}
            className="border-neutral-700 text-white hover:bg-neutral-900"
            data-testid="open-global-blackouts-btn"
          >
            <Globe className="w-4 h-4 mr-2" /> Trial blackouts
          </Button>
          <Button
            onClick={() => { setEditingItem(null); resetClassForm(); setClassModalOpen(true); }}
            className="bg-[#FF007F] hover:bg-[#D6006A]"
            data-testid="add-class-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Class
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {classes.map((cls, index) => (
          <Card key={cls.id} className="bg-[#111] border-[#333]">
            <CardContent className="flex items-center justify-between p-4">
              {/* Reorder buttons on the left */}
              <div className="flex flex-col gap-1 mr-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleReorderClass(cls.id, 'up')}
                  disabled={index === 0}
                  className="h-6 w-6 text-white/30 hover:text-white disabled:opacity-20"
                  data-testid={`move-up-${cls.id}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleReorderClass(cls.id, 'down')}
                  disabled={index === classes.length - 1}
                  className="h-6 w-6 text-white/30 hover:text-white disabled:opacity-20"
                  data-testid={`move-down-${cls.id}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-4 flex-1">
                {cls.image_url && (
                  <img src={cls.image_url} alt={cls.name} className="w-16 h-16 object-cover rounded-lg" />
                )}
                <div>
                  <h3 className="font-semibold text-white">{cls.name}</h3>
                  <p className="text-sm text-white/60">
                    {cls.category} • Ages {cls.age_min}-{cls.age_max} • {cls.capacity} spots
                  </p>
                  {(cls.day_of_week || cls.start_time) && (
                    <p className="text-xs text-cyan-400 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {cls.day_of_week && <span>{cls.day_of_week}</span>}
                      {cls.start_time && cls.end_time && <span>{cls.start_time} - {cls.end_time}</span>}
                      {cls.start_time && !cls.end_time && <span>{cls.start_time}</span>}
                    </p>
                  )}
                  {cls.term_dates && (
                    <p className="text-xs text-purple-400 flex items-center gap-1 mt-0.5">
                      <Calendar className="w-3 h-3" /> {cls.term_dates}
                    </p>
                  )}
                  {cls.address && (
                    <p className="text-xs text-white/50 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {cls.address}
                    </p>
                  )}
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {cls.is_visible === false && (
                      <Badge className="bg-yellow-500/20 text-yellow-300 text-xs">
                        Hidden
                      </Badge>
                    )}
                    {cls.offers_free_trial && (
                      <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">
                        Free Trial
                      </Badge>
                    )}
                    {cls.offers_one_off_booking && cls.one_off_price > 0 && (
                      <Badge className="bg-orange-500/20 text-orange-300 text-xs">
                        Drop-in
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right space-y-0.5">
                  {cls.six_week_price && (
                    <p className="font-mono text-sm text-emerald-400">
                      6 Weeks: {formatPrice(cls.six_week_price)}
                    </p>
                  )}
                  {cls.term_price && (
                    <p className="font-mono text-sm text-blue-400">
                      Term: {formatPrice(cls.term_price)}
                    </p>
                  )}
                  {cls.subscription_price && (
                    <p className="font-mono text-sm text-purple-400">
                      Monthly: £{cls.subscription_price}
                    </p>
                  )}
                  {cls.one_off_price && (
                    <p className="font-mono text-sm text-cyan-400">
                      One-off: {formatPrice(cls.one_off_price)}
                    </p>
                  )}
                  {!cls.six_week_price && !cls.term_price && !cls.subscription_price && !cls.one_off_price && (
                    <p className="text-xs text-white/30 italic">No pricing set</p>
                  )}
                  <p className="text-xs text-white/50">
                    {cls.duration_minutes} mins
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => editClass(cls)}
                    className="text-white/50 hover:text-white"
                    data-testid={`edit-class-${cls.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteClass(cls.id)}
                    className="text-red-400/50 hover:text-red-400"
                    data-testid={`delete-class-${cls.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {classes.length === 0 && (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="py-12 text-center text-white/50">
              No classes yet. Create your first class!
            </CardContent>
          </Card>
        )}
      </div>

      {/* Class Modal */}
      <Dialog open={classModalOpen} onOpenChange={setClassModalOpen}>
        <DialogContent className="max-w-2xl bg-[#111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? 'Edit Class' : 'Add New Class'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Name *</Label>
                <Input 
                  value={classForm.name} 
                  onChange={e => setClassForm({...classForm, name: e.target.value})} 
                  placeholder="Ballet Basics" 
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Category *</Label>
                <Select 
                  value={classForm.category} 
                  onValueChange={v => setClassForm({...classForm, category: v})}
                >
                  <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea 
                value={classForm.description} 
                onChange={e => setClassForm({...classForm, description: e.target.value})} 
                placeholder="Class description..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Min Age</Label>
                <Input 
                  type="number" 
                  value={classForm.age_min} 
                  onChange={e => setClassForm({...classForm, age_min: parseInt(e.target.value)})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Max Age</Label>
                <Input 
                  type="number" 
                  value={classForm.age_max} 
                  onChange={e => setClassForm({...classForm, age_max: parseInt(e.target.value)})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Capacity</Label>
                <Input 
                  type="number" 
                  value={classForm.capacity} 
                  onChange={e => setClassForm({...classForm, capacity: parseInt(e.target.value)})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Duration (mins)</Label>
                <Input 
                  type="number" 
                  value={classForm.duration_minutes} 
                  onChange={e => setClassForm({...classForm, duration_minutes: parseInt(e.target.value)})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">6-Week Price (£) <span className="text-white/40 text-xs font-normal">recommended</span></Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={classForm.six_week_price || ''} 
                  onChange={e => setClassForm({...classForm, six_week_price: e.target.value ? parseFloat(e.target.value) : null})}
                  placeholder="Price for 6-week billing cycle"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-six-week-price-input"
                />
                <p className="text-xs text-white/40">Used for regular 6-week payment cycles</p>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Term Price (£) <span className="text-white/40 text-xs font-normal">optional</span></Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={classForm.term_price || ''} 
                  onChange={e => setClassForm({...classForm, term_price: e.target.value ? parseFloat(e.target.value) : null})}
                  placeholder="Leave empty to use 6-week pricing"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-term-price-input"
                />
                <p className="text-xs text-white/40">Alternative full-term pricing (if different)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">One-Off Price (£) <span className="text-white/40 text-xs font-normal">optional</span></Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={classForm.one_off_price || ''} 
                  onChange={e => setClassForm({...classForm, one_off_price: e.target.value ? parseFloat(e.target.value) : null})}
                  placeholder="Leave empty if not applicable"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-one-off-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Monthly Price (£) <span className="text-white/40 text-xs font-normal">optional</span></Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={classForm.subscription_price || ''} 
                  onChange={e => setClassForm({...classForm, subscription_price: e.target.value ? parseFloat(e.target.value) : null})}
                  placeholder="Leave empty if not applicable"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-subscription-price-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Room</Label>
                <Select 
                  value={classForm.room_id} 
                  onValueChange={v => setClassForm({...classForm, room_id: v})}
                >
                  <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map(room => (
                      <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address/Location */}
            <div className="space-y-2">
              <Label className="text-white/70 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Class Location/Address <span className="text-white/40 text-xs font-normal">optional</span>
              </Label>
              <Input 
                value={classForm.address || ''} 
                onChange={e => setClassForm({...classForm, address: e.target.value || null})}
                placeholder="e.g., Kents Hill Community Centre, MK7 6HB"
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="class-address-input"
              />
            </div>

            {/* Day, Time & Dates */}
            <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30 space-y-4">
              <Label className="text-cyan-300 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Schedule Information <span className="text-white/40 text-xs font-normal">optional</span>
              </Label>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Day(s)</Label>
                  <Input 
                    value={classForm.day_of_week || ''} 
                    onChange={e => setClassForm({...classForm, day_of_week: e.target.value || null})}
                    placeholder="e.g., Monday or Mon & Wed"
                    className="bg-[#0a0a0a] border-[#333] text-white"
                    data-testid="class-day-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Start Time</Label>
                  <Input 
                    type="time"
                    value={classForm.start_time || ''} 
                    onChange={e => setClassForm({...classForm, start_time: e.target.value || null})}
                    className="bg-[#0a0a0a] border-[#333] text-white"
                    data-testid="class-start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">End Time</Label>
                  <Input 
                    type="time"
                    value={classForm.end_time || ''} 
                    onChange={e => setClassForm({...classForm, end_time: e.target.value || null})}
                    className="bg-[#0a0a0a] border-[#333] text-white"
                    data-testid="class-end-time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Term/Course Dates
                </Label>
                <Input 
                  value={classForm.term_dates || ''} 
                  onChange={e => setClassForm({...classForm, term_dates: e.target.value || null})}
                  placeholder="e.g., Jan 6 - Mar 15, 2026 or Starting January 2026"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="class-term-dates-input"
                />
              </div>
            </div>

            {/* Free Trial Toggle */}
            <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={classForm.offers_free_trial !== false}
                  onChange={e => setClassForm({...classForm, offers_free_trial: e.target.checked})}
                  className="w-4 h-4 rounded"
                  data-testid="class-offers-free-trial"
                />
                <Label className="text-emerald-300">Offer Free Trial for this class</Label>
              </div>
              <p className="text-xs text-white/40 mt-2 ml-7">
                When enabled, this class will appear in the free trial booking page.
              </p>
            </div>

            {/* Trial availability — per-class blocklist of upcoming dates.
                Only rendered when Free Trial is enabled AND we can compute
                dates from day_of_week + term_dates. */}
            {classForm.offers_free_trial !== false && (() => {
              const dates = upcomingClassDates(classForm, { maxCount: 20 });
              if (!classForm.day_of_week || !classForm.term_dates) {
                return (
                  <div className="p-4 bg-neutral-900/50 rounded-lg border border-neutral-800" data-testid="class-trial-availability-hint">
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <CalendarX className="w-4 h-4" /> Trial availability
                    </div>
                    <p className="text-xs text-white/40 mt-2">
                      Set a day of week AND term dates above to manage which specific trial dates are open.
                    </p>
                  </div>
                );
              }
              const blocked = Array.isArray(classForm.blocked_trial_dates) ? classForm.blocked_trial_dates : [];
              const toggle = (iso) => {
                const next = blocked.includes(iso)
                  ? blocked.filter((d) => d !== iso)
                  : [...blocked, iso].sort();
                setClassForm({ ...classForm, blocked_trial_dates: next });
              };
              return (
                <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30" data-testid="class-trial-availability">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-cyan-300 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Trial availability
                    </Label>
                    <button
                      type="button"
                      onClick={() => setClassForm({ ...classForm, blocked_trial_dates: [] })}
                      className="text-xs text-cyan-300/70 hover:text-cyan-300 underline underline-offset-2"
                      disabled={blocked.length === 0}
                      data-testid="class-trial-availability-clear"
                    >
                      Reset (open all)
                    </button>
                  </div>
                  <p className="text-xs text-white/50 mb-3">
                    Untick any dates you don&apos;t want available for free trials (e.g. school holidays).
                    {dates.length ? ` Showing next ${dates.length} sessions.` : ''}
                  </p>
                  {dates.length === 0 ? (
                    <p className="text-xs text-amber-300">
                      No upcoming sessions found — check the day of week and term dates.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                      {dates.map((iso) => {
                        const off = blocked.includes(iso);
                        return (
                          <label
                            key={iso}
                            className={
                              'flex items-center gap-2 text-xs rounded-md border px-2 py-1.5 cursor-pointer transition-colors ' +
                              (off
                                ? 'bg-neutral-900/50 border-neutral-800 text-white/40 line-through'
                                : 'bg-cyan-500/5 border-cyan-500/40 text-white hover:bg-cyan-500/15')
                            }
                            data-testid={`class-trial-date-${iso}`}
                          >
                            <input
                              type="checkbox"
                              checked={!off}
                              onChange={() => toggle(iso)}
                              className="w-3.5 h-3.5"
                            />
                            <span>{formatTrialDate(iso)}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* One-Off Booking Toggle */}
            <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={classForm.offers_one_off_booking === true}
                  onChange={e => setClassForm({...classForm, offers_one_off_booking: e.target.checked})}
                  className="w-4 h-4 rounded"
                  data-testid="class-offers-one-off"
                />
                <Label className="text-orange-300">Allow One-Off Bookings</Label>
              </div>
              <p className="text-xs text-white/40 mt-2 ml-7">
                When enabled, enrolled parents can book individual drop-in sessions for this class.
                {!classForm.one_off_price && <span className="text-orange-400 ml-1">(Set a one-off price above to enable)</span>}
              </p>
            </div>

            {/* Visibility Toggle */}
            <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={classForm.is_visible !== false}
                  onChange={e => setClassForm({...classForm, is_visible: e.target.checked})}
                  className="w-4 h-4 rounded"
                  data-testid="class-visibility-toggle"
                />
                <div>
                  <Label className="text-yellow-300">Visible on Public Site</Label>
                  <p className="text-xs text-white/50 mt-1">
                    Uncheck to hide this class from the public schedule and class listings
                  </p>
                </div>
              </div>
            </div>

            {/* Teachers */}
            <div className="space-y-2">
              <Label className="text-white/70">Assign Teachers</Label>
              {teachers.length === 0 ? (
                <p className="text-white/40 text-sm italic">
                  No teachers available. Teachers will appear here once they complete their account setup.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {teachers.map(teacher => {
                    const assigned = classForm.teacher_ids?.find(t => t.teacher_id === teacher.id);
                    return (
                      <div key={teacher.id} className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant={assigned ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleTeacherAssignment(teacher.id)}
                          className={assigned ? "bg-[#FF007F] hover:bg-[#D6006A]" : "border-[#333] text-white/70"}
                        >
                          {teacher.name}
                        </Button>
                        {assigned && (
                          <Select 
                            value={assigned.role} 
                            onValueChange={v => updateTeacherRole(teacher.id, v)}
                          >
                            <SelectTrigger className="w-24 h-8 bg-[#0a0a0a] border-[#333] text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lead">Lead</SelectItem>
                              <SelectItem value="assistant">Assistant</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Class Photo */}
            <div className="space-y-2">
              <Label className="text-white/70">Class Photo</Label>
              <div className="flex items-center gap-4">
                {classForm.image_url && (
                  <img src={classForm.image_url} alt="Preview" className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1 space-y-2">
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleClassPhotoUpload}
                      disabled={uploadingClassPhoto}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploadingClassPhoto}
                      className="border-[#333] text-white/70 cursor-pointer"
                      asChild
                    >
                      <span>
                        {uploadingClassPhoto ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Camera className="w-4 h-4 mr-2" /> {classForm.image_url ? 'Change Photo' : 'Upload Photo'}</>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
              <Input 
                value={classForm.image_url} 
                onChange={e => setClassForm({...classForm, image_url: e.target.value})} 
                placeholder="Or paste URL..." 
                className="bg-[#0a0a0a] border-[#333] text-white text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClassModalOpen(false)} className="border-[#333] text-white/70">
              Cancel
            </Button>
            <Button onClick={handleClassSubmit} className="bg-[#FF007F] hover:bg-[#D6006A]" data-testid="save-class-btn">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global school-wide trial blackout dates dialog */}
      <Dialog open={blackoutOpen} onOpenChange={setBlackoutOpen}>
        <DialogContent
          className="bg-neutral-950 border-neutral-800 text-white max-w-lg"
          data-testid="global-blackouts-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Globe className="w-4 h-4 text-cyan-300" /> Global trial blackout dates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/60">
              Dates listed here block free trials across EVERY class (e.g. holidays,
              teacher training, show weekends). Per-class blocklists inside the
              Edit-class dialog are applied on top.
            </p>
            <div className="flex gap-2">
              <Input
                type="date"
                value={blackoutInput}
                onChange={(e) => setBlackoutInput(e.target.value)}
                className="bg-neutral-900 border-neutral-700 text-white"
                data-testid="global-blackouts-input"
              />
              <Button
                type="button"
                onClick={addBlackout}
                className="bg-cyan-500 hover:bg-cyan-600 text-neutral-950 shrink-0"
                data-testid="global-blackouts-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            {blackoutDates.length === 0 ? (
              <p className="text-xs text-white/40 py-4 text-center border border-dashed border-neutral-800 rounded-md">
                No blackout dates set. Every class-specific trial date is available.
              </p>
            ) : (
              <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="global-blackouts-list">
                {blackoutDates.map((iso) => (
                  <li
                    key={iso}
                    className="flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2"
                    data-testid={`global-blackout-${iso}`}
                  >
                    <span className="text-sm text-white">{formatTrialDate(iso)}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBlackout(iso)}
                      className="text-red-400 hover:text-red-300 h-7 px-2"
                      data-testid={`global-blackout-remove-${iso}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBlackoutOpen(false)}
              className="border-neutral-700 text-white"
              data-testid="global-blackouts-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveBlackouts}
              disabled={blackoutSaving}
              className="bg-cyan-500 hover:bg-cyan-600 text-neutral-950"
              data-testid="global-blackouts-save-btn"
            >
              {blackoutSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassesTab;
